import React from 'react';
import t from '@guestyci/localize/t.macro';
import { Text, Heading, Stack, Button } from '@guestyci/arc';
import { Fingerprint } from '@guestyci/icons/system';
import { WIDGET_STATES } from '../../config/widgetStates';
import { FormLayout } from '../../layout';
import VerifyFactorSwitch from '../VerifyFactorSwitch';

const BiometricStep = ({
  isVerifyMode,
  onRetry,
  onPickDifferentMethod,
  error,
  isSubmitting,
  alternateVerifyFactors,
  onSwitchVerifyFactor,
}) => {
  const isWaitingForBrowser = isSubmitting && !error;

  const heading = isVerifyMode
    ? t('Verify with your biometric login or security key')
    : t('Set up a biometric login or security key');

  const description = isVerifyMode
    ? t('Your browser will ask you to confirm your identity with a biometric login or security key, such as Touch ID or Face ID.')
    : t('Your browser will ask you to set up a biometric login or security key, such as Touch ID or Face ID.');

  const secondaryLabel = isVerifyMode
    ? t('Back to login')
    : t('Pick a different authentication method');

  const getPrimaryLabel = () => {
    if (isWaitingForBrowser) return t('Waiting for your browser');
    if (error) return t('Try again');
    return isVerifyMode ? t('Verify') : t('Continue');
  };

  return (
    <Stack spacing={6} justify="between" className="gst-w-full gst-flex-1">
      <Stack spacing={6}>
        <Stack align="center" justify="center" className="gst-rounded-full gst-bg-success-foreground gst-w-20 gst-h-20 gst-shrink-0">
          <Fingerprint className="gst-size-6 gst-text-primary" aria-hidden="true" />
        </Stack>
        <Stack spacing={4}>
          <Heading variant="h2">{heading}</Heading>
          <Text type="secondary">{description}</Text>
        </Stack>

        {error && (
          <Text className="gst-text-destructive">{error}</Text>
        )}
      </Stack>

      <Stack spacing={4} className="gst-w-full">
        <Stack spacing={2} className="gst-w-full">
          <Button
            type="button"
            className="gst-w-full"
            onClick={onRetry}
            isLoading={isWaitingForBrowser}
          >
            {getPrimaryLabel()}
          </Button>
          {isVerifyMode && !isWaitingForBrowser && (
            <VerifyFactorSwitch
              factors={alternateVerifyFactors}
              onSelect={onSwitchVerifyFactor}
              disabled={isSubmitting}
            />
          )}
          <Button type="button" variant="plain" className="gst-w-full" onClick={onPickDifferentMethod}>
            {secondaryLabel}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

const CustomBiometricMFA = ({
  oktaViewState,
  onRetry,
  onPickDifferentMethod,
  error,
  isSubmitting,
  alternateVerifyFactors,
  onSwitchVerifyFactor,
}) => {
  const isVerifyMode = oktaViewState === WIDGET_STATES.MFA_VERIFY_WEBAUTHN;

  return (
    <FormLayout>
      <BiometricStep
        isVerifyMode={isVerifyMode}
        onRetry={onRetry}
        onPickDifferentMethod={onPickDifferentMethod}
        error={error}
        isSubmitting={isSubmitting}
        alternateVerifyFactors={alternateVerifyFactors}
        onSwitchVerifyFactor={onSwitchVerifyFactor}
      />
    </FormLayout>
  );
};

export default CustomBiometricMFA;
